import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiCheckLine, RiLoader2Fill, RiPlugLine, RiQuestionLine, RiRefreshLine, RiSave2Line, RiServerLine } from '@remixicon/react';
import { CONNECTOR_CHANNEL_PREFIX, CONNECTOR_HYDRO_KEYS, CONNECTOR_SITE_CODE_RE, type AccessibleTenant } from '@atnt/shared';
import { ApiError } from '@/shared/api/client';
import { Badge, Button, Card, DataTable, EmptyState, Field, Input, Skeleton, toast, type BadgeColor, type DataTableColumn } from '@/shared/ui';
import { StatusBadge, useLiveConnection, useSensorValues, withUnit } from '@/features/constructor/engine';
import { useDevices } from '@/features/devices/api';
import { ComboInput } from '@/features/devices/components/ComboInput';
import { ErrorBanner, NoResults, RegistryToolbar } from '@/features/devices/components/parts';
import { formatReading, formatReadingTime, matches } from '@/features/devices/format';
import { indicatorRefs, type IndicatorRef } from '@/features/devices/options';
import { useHotkey } from '@/features/settings/hotkeys';
import { useTableDensity } from '@/features/settings/preferences';
import { useConnectorStatus, useSaveChannel, useSaveSiteId, useTenantDetails } from '../api';

/**
 * The REAL parts of the ATNT unified-server connector (first version of "Konnektorlar boshqaruvi", 03-modules §1.2):
 * live source status of the API, the reservoir's unified-server site code (organization admins) and the channel
 * mapping of physical indicators to `hydro:<KEY>` (`connectors.update`). Shown inside the One Server connector's
 * detail page ("Umumiy", "Sozlamalar", "Kanallar"). Needs a <LiveProvider> above (browser connection state, values).
 */

export const CHANNELS = CONNECTOR_HYDRO_KEYS.map((k) => `${CONNECTOR_CHANNEL_PREFIX}${k}`);
export const isKnownChannel = (c: string | null | undefined) =>
  Boolean(c && c.toLowerCase().startsWith(CONNECTOR_CHANNEL_PREFIX) && (CONNECTOR_HYDRO_KEYS as readonly string[]).includes(c.slice(CONNECTOR_CHANNEL_PREFIX.length).trim().toUpperCase()));

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-24 items-center justify-between gap-12">
      <dt className="text-xs text-fg-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-right text-13 font-medium text-fg">{children}</dd>
    </div>
  );
}

export function SourceCard({ tenant }: { tenant: AccessibleTenant }) {
  const { t } = useTranslation('connectors');
  const status = useConnectorStatus(tenant.id);
  const connection = useLiveConnection();
  const s = status.data;
  const state: { color: BadgeColor; label: string } = !s?.source
    ? { color: 'neutral', label: t('status.off') }
    : s.health?.ok
      ? { color: 'green', label: t('status.ok') }
      : { color: 'red', label: t('status.error') };
  const interval = s?.intervalMs ?? null;
  const every =
    interval == null
      ? '—'
      : interval >= 60_000
        ? t('every', { value: t('minutes', { count: Math.round(interval / 60_000) }) })
        : t('every', { value: t('seconds', { count: Math.round(interval / 1000) }) });
  const wsColor: BadgeColor = connection === 'open' ? 'green' : connection === 'reconnecting' ? 'red' : 'neutral';
  return (
    <Card
      title={t('source.title')}
      description={t('source.desc')}
      actions={
        <Button size="sm" variant="secondary" leftIcon={RiRefreshLine} onClick={() => void status.refetch()} loading={status.isFetching} aria-label={t('refresh')}>
          {t('refresh')}
        </Button>
      }
    >
      {status.isError ? (
        <ErrorBanner onRetry={() => void status.refetch()} />
      ) : !s ? (
        <div className="flex flex-col gap-10">
          <Skeleton className="h-16" />
          <Skeleton className="h-16 w-4/5" />
          <Skeleton className="h-16 w-3/5" />
        </div>
      ) : (
        <dl className="flex flex-col gap-8">
          <Row label={t('source.name')}>
            <span className="inline-flex items-center gap-8">
              <RiServerLine aria-hidden className="size-16 text-icon" />
              {s.source ? t(`source.${s.source}`) : t(`source.${s.configured}`)}
            </span>
          </Row>
          <Row label={t('source.state')}>
            <Badge color={state.color}>{state.label}</Badge>
          </Row>
          <Row label={t('source.lastSuccess')}>{formatReadingTime(s.health?.lastSuccessAt)}</Row>
          <Row label={t('source.interval')}>{every}</Row>
          <Row label={t('source.bound')}>{t('source.boundValue', { bound: s.boundIndicators, total: s.physicalIndicators })}</Row>
          <Row label={t('source.browser')}>
            <Badge color={wsColor}>{t(`ws.${connection}`)}</Badge>
          </Row>
          {(s.health?.lastError || (!s.source && s.configured === 'unified')) && (
            <p role="alert" className="rounded-8 bg-tone-red-10 px-10 py-6 text-xs text-tone-red-lighter">
              {s.health?.lastError ?? t('source.notConfigured')}
            </p>
          )}
        </dl>
      )}
    </Card>
  );
}

export function SiteCard({ tenant, editable }: { tenant: AccessibleTenant; editable: boolean }) {
  const { t } = useTranslation('connectors');
  const details = useTenantDetails(tenant.id);
  const save = useSaveSiteId(tenant.id);
  const saved = details.data?.externalSiteId ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const value = draft ?? saved;
  const dirty = draft != null && draft.trim() !== saved;

  const submit = async () => {
    const next = value.trim();
    if (next && !CONNECTOR_SITE_CODE_RE.test(next)) return setError(t('site.invalid'));
    try {
      await save.mutateAsync(next || null);
      setDraft(null);
      setError(null);
      toast.success(t('site.saved'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError(t('site.orgOnly'));
      else setError(err instanceof Error ? err.message : t('error'));
    }
  };

  return (
    <Card title={t('site.title')} description={t('site.desc')}>
      <form
        className="flex flex-col gap-10"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (editable && dirty) void submit();
        }}
      >
        <Field label={t('site.label')} error={error} hint={editable ? undefined : t('site.orgOnly')}>
          {details.isLoading ? (
            <Skeleton className="h-32 rounded-8" />
          ) : (
            <Input
              value={value}
              readOnly={!editable}
              maxLength={128}
              spellCheck={false}
              autoComplete="off"
              placeholder={editable ? t('site.placeholder') : t('site.empty')}
              onChange={(e) => {
                setDraft(e.currentTarget.value.toLowerCase());
                setError(null);
              }}
              leftIcon={RiPlugLine}
            />
          )}
        </Field>
        {editable && (
          <div className="flex justify-end gap-8">
            <Button
              variant="secondary"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button variant="primary-alt" type="submit" leftIcon={RiSave2Line} disabled={!dirty} loading={save.isPending}>
              {t('save')}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

export function HelpCard() {
  const { t } = useTranslation('connectors');
  const items = t('help.items', { returnObjects: true }) as string[];
  return (
    <Card
      title={
        <span className="flex items-center gap-8">
          <RiQuestionLine aria-hidden className="size-18 text-accent-2" />
          {t('help.title')}
        </span>
      }
    >
      <ol className="flex list-decimal flex-col gap-6 pl-18 text-xs leading-17 text-fg-secondary marker:text-fg-tertiary">
        {(Array.isArray(items) ? items : []).map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ol>
    </Card>
  );
}

export function ChannelsCard({ tenant, editable }: { tenant: AccessibleTenant; editable: boolean }) {
  const { t, i18n } = useTranslation('connectors');
  const density = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const devices = useDevices(tenant.id);
  const refs = indicatorRefs(devices.data, 'physical');
  const readings = useSensorValues(refs.map((r) => r.indicator.id));
  const rows = refs.filter(
    (r) => matches(r.device.name, search) || matches(r.indicator.name, search) || matches(r.indicator.externalChannelId, search),
  );

  useHotkey('search', () => searchRef.current?.focus());
  useHotkey('refresh', () => void devices.refetch());

  const columns: DataTableColumn<IndicatorRef>[] = [
    { id: 'device', header: t('channels.device'), tone: 'primary', cell: (r) => <span className="truncate">{r.device.name}</span> },
    { id: 'indicator', header: t('channels.indicator'), tone: 'primary', cell: (r) => <span className="truncate">{r.indicator.name}</span> },
    {
      id: 'channel',
      header: t('channels.channel'),
      width: 300,
      cell: (r) => <ChannelCell tenantId={tenant.id} item={r} editable={editable} />,
    },
    {
      id: 'binding',
      header: t('channels.binding'),
      width: 170,
      cell: (r) =>
        !r.indicator.externalChannelId ? (
          <Badge color="neutral">{t('channels.none')}</Badge>
        ) : isKnownChannel(r.indicator.externalChannelId) ? (
          <Badge color="green">{t('channels.bound')}</Badge>
        ) : (
          <Badge color="yellow">{t('channels.unknown')}</Badge>
        ),
    },
    {
      id: 'value',
      header: t('channels.value'),
      tone: 'primary',
      cell: (r) => {
        const v = readings.get(r.indicator.id);
        return <span className="truncate tabular-nums">{withUnit(formatReading(v?.value ?? r.indicator.lastValue, r.indicator.precision, i18n.language), r.indicator.unit)}</span>;
      },
    },
    { id: 'time', header: t('channels.time'), tone: 'secondary', cell: (r) => formatReadingTime(readings.get(r.indicator.id)?.ts ?? r.indicator.lastValueAt) },
    { id: 'status', header: t('channels.status'), width: 150, cell: (r) => <StatusBadge status={readings.get(r.indicator.id)?.status ?? 'unknown'} /> },
  ];

  return (
    <Card className="min-h-0 flex-1 gap-10" title={t('channels.title')} description={editable ? t('channels.desc') : t('channels.readOnly')}>
      <RegistryToolbar search={search} onSearch={setSearch} placeholder={t('channels.search')} total={devices.data ? rows.length : null} searchRef={searchRef} />
      {devices.isError ? (
        <ErrorBanner onRetry={() => void devices.refetch()} />
      ) : (
        <DataTable
          aria-label={t('channels.title')}
          columns={columns}
          rows={rows}
          getRowId={(r) => r.indicator.id}
          indexColumn={{ header: t('index'), format: (i) => `${i + 1}.` }}
          loading={devices.isLoading}
          density={density}
          minWidth={1200}
          empty={search ? <NoResults onClear={() => setSearch('')} /> : <EmptyState icon={RiPlugLine} title={t('channels.empty')} />}
        />
      )}
    </Card>
  );
}

/** Inline channel editor: pick `hydro:<KEY>` or type another id; Enter / blur saves, Esc reverts. */
function ChannelCell({ tenantId, item, editable }: { tenantId: string; item: IndicatorRef; editable: boolean }) {
  const { t } = useTranslation('connectors');
  const save = useSaveChannel(tenantId);
  const current = item.indicator.externalChannelId ?? '';
  const [draft, setDraft] = useState(current);
  const [done, setDone] = useState(false);
  // follow a mapping saved elsewhere (refetch) — adjust state during render instead of in an effect
  const [shown, setShown] = useState(current);
  if (shown !== current) {
    setShown(current);
    setDraft(current);
  }
  useEffect(() => {
    if (!done) return;
    const h = window.setTimeout(() => setDone(false), 1500);
    return () => window.clearTimeout(h);
  }, [done]);

  if (!editable) return current ? <span className="truncate font-medium text-fg">{current}</span> : <span className="text-fg-tertiary">—</span>;

  const commit = async (value: string) => {
    const next = value.trim();
    if (next === current) return;
    try {
      await save.mutateAsync({ deviceId: item.device.id, indicatorId: item.indicator.id, channel: next || null });
      setDone(true);
      toast.success(t('channels.saved'));
    } catch (err) {
      setDraft(current);
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- wrapper commits the inline edit on blur / Enter; the combobox inside is the control
    <div
      className="flex w-full items-center gap-6"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void commit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit(draft);
        }
        if (e.key === 'Escape') setDraft(current);
      }}
    >
      <ComboInput
        className="h-28"
        value={draft}
        onValueChange={(v) => {
          setDraft(v);
          if (CHANNELS.includes(v)) void commit(v);
        }}
        options={CHANNELS}
        placeholder={t('channels.placeholder')}
        aria-label={`${t('channels.channel')}: ${item.device.name} · ${item.indicator.name}`}
        maxLength={128}
      />
      <span className="flex size-16 shrink-0 items-center justify-center" aria-live="polite">
        {save.isPending ? (
          <RiLoader2Fill aria-label={t('saving')} className="size-16 animate-spin text-icon" />
        ) : done ? (
          <RiCheckLine aria-label={t('channels.saved')} className="size-16 text-tone-green" />
        ) : null}
      </span>
    </div>
  );
}
